import React, {useEffect, useState} from 'react';
import {Trans} from 'react-i18next';
import classnames from 'classnames';
import '../index.css';
import Loader from '../../../../../../components/spinner/Loader';
import useWindowHeight from '../../../../../../components/hook/useWindowHeight';
import useWindowWidth from '../../../../../../components/hook/useWindowWidth';
import {getPrimaryColor} from '../../../config/utils/commonUtils';
import {ReactComponent as ModeSVG} from '../../../../../../assets/images/skincare/face_assessment/ico_simulation_mode.svg';
import SimulationDisplayFixedArch from '../../../simulationpage/preview/SiumlationDisplayFixedArch';
import PercentageMask from './PercentageMask';

export default function FaceAssessmentModule({
  commonData = {},
  categories,
  isLandscape,
  enable,
  isExpand = false,
}) {
  const [showModuleInfoModal, setShowModuleInfoModal] = useState(false);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [selectCategory, setSelectCategory] = useState('');
  const [ratio, setRatio] = useState(null);
  const [loaded, setLoaded] = useState(true);
  const windowHeight = useWindowHeight();
  const windowWidth = useWindowWidth();

  const primaryColor = getPrimaryColor(commonData);

  useEffect(() => {
    if (loaded) return;
    const slider = document.querySelector(
      isLandscape
        ? '.face_assessment_preview-display-section-landscape'
        : '.face_assessment_preview-display-section'
    );

    if (!slider) return;

    let isDown = false;
    let startX;
    let scrollLeft;

    const handleMouseDown = e => {
      isDown = true;
      slider.classList.add('active');
      startX = e.pageX - slider.offsetLeft;
      scrollLeft = slider.scrollLeft;
    };

    const handleMouseLeaveOrUp = () => {
      isDown = false;
      slider.classList.remove('active');
    };

    const handleMouseMove = e => {
      if (!isDown) return;
      e.preventDefault();
      const x = e.pageX - slider.offsetLeft;
      const walk = (x - startX) * 2;
      slider.scrollLeft = scrollLeft - walk;
    };

    slider.addEventListener('mousedown', handleMouseDown);
    slider.addEventListener('mouseleave', handleMouseLeaveOrUp);
    slider.addEventListener('mouseup', handleMouseLeaveOrUp);
    slider.addEventListener('mousemove', handleMouseMove);

    return () => {
      slider.removeEventListener('mousedown', handleMouseDown);
      slider.removeEventListener('mouseleave', handleMouseLeaveOrUp);
      slider.removeEventListener('mouseup', handleMouseLeaveOrUp);
      slider.removeEventListener('mousemove', handleMouseMove);
    };
  }, [loaded, isLandscape]);

  useEffect(() => {
    if (categories.length > 0) {
      setCategoryOptions(categories);
      const initialCategory =
        categories.find(r => r.value === selectCategory.value) || categories[0];

      setSelectCategory(initialCategory);
      setRatio(initialCategory.ratio);
      setLoaded(false);
    }
  }, [categories, selectCategory.value]);

  function calculateTransform(windowWidth, windowHeight, isLandscape) {
    return `scale(${
      windowWidth > windowHeight
        ? isLandscape
          ? windowHeight * 0.002
          : windowHeight * 0.0016
        : isLandscape
        ? windowWidth * 0.0018
        : windowWidth * 0.002
    })`;
  }

  return loaded ? (
    <Loader />
  ) : (
    <div
      className={classnames({
        'shadow-lg': true,
        'face_assessment_preview-module': !isLandscape,
        'face_assessment_preview-module-landscape': isLandscape,
      })}
      style={
        isExpand
          ? {
              transform: calculateTransform(
                windowWidth,
                windowHeight,
                isLandscape
              ),
              transformOrigin: 'center center',
            }
          : undefined
      }
    >
      {showModuleInfoModal && (
        <div className="face_assessment_preview-module-overlay">
          <div className="face_assessment_preview-module-popup">
            <div className="d-flex justify-content-end">
              <i
                className="fa fa-times cursor-pointer"
                onClick={() => setShowModuleInfoModal(false)}
              />
            </div>
            <div className="face_assessment_preview-module-popup-title">
              {selectCategory.label}
            </div>
            <div className="face_assessment_preview-module-popup-image">
              <img src={selectCategory.image_info} alt="" />
            </div>
            <div className="face_assessment_preview-module-popup-desc">
              <div className="face_assessment_preview-module-popup-desc1">
                {selectCategory.desc1}
              </div>
              <div className="face_assessment_preview-module-popup-desc2">
                {selectCategory.desc2}
              </div>
            </div>
          </div>
        </div>
      )}
      <div
        className={
          isLandscape
            ? 'face_assessment_preview-model-landscape'
            : 'face_assessment_preview-model'
        }
        style={{
          backgroundImage: enable
            ? isLandscape
              ? `url(${selectCategory.image_landscape})`
              : `url(${selectCategory.image_portrait})`
            : undefined,
        }}
      >
        {enable && (
          <PercentageMask
            isLandscape={isLandscape}
            selectCategory={selectCategory}
            primaryColor={primaryColor}
          />
        )}
        {!enable && (
          <SimulationDisplayFixedArch
            isLandscape={isLandscape}
            primaryColor={primaryColor}
          />
        )}
        <div
          className="face_assessment_preview-title"
          style={{display: !enable && 'none'}}
        >
          {selectCategory.label}&nbsp;
          <span
            className="cursor-pointer"
            onClick={() => setShowModuleInfoModal(true)}
          >
            <i className="fa fa-info-circle" />
          </span>
        </div>

        <div className="face_assessment_preview-mode-container">
          <div className="face_assessment_preview-mode-image">
            <div
              className={classnames({
                'face_assessment_preview-mode-image-container': !isLandscape,
                'face_assessment_preview-mode-image-container-landscape': isLandscape,
                'face_assessment_preview-mode-image-container-disable':
                  !enable && !isLandscape,
                'face_assessment_preview-mode-image-container-landscape-disable':
                  !enable && isLandscape,
              })}
            >
              <div className="face_assessment_preview-mode-mode-icon">
                <ModeSVG fill={enable ? primaryColor : '#333'} />
              </div>
            </div>
          </div>
        </div>

        {enable && (
          <div className="face_assessment_preview-button-container">
            <div
              className="face_assessment_preview-button"
              style={{backgroundColor: primaryColor}}
            >
              <Trans i18nKey="p.s.uiconfiguration.face.assessment.preview.button">
                Post-Simulation = {{ratio}}
              </Trans>
            </div>
          </div>
        )}
      </div>

      <div
        className={
          isLandscape
            ? 'face_assessment_preview-display-section-landscape'
            : 'face_assessment_preview-display-section'
        }
        style={isExpand ? {scrollbarWidth: 'auto'} : {}}
      >
        <div className="face_assessment_preview-archbase">
          <div className="face_assessment_preview-choose-section">
            <div className="face_assessment_preview-treatment-selector">
              {categoryOptions.map((ts, index) => (
                <div className="d-flex align-items-center" key={ts.value}>
                  {index > 0 && categoryOptions[index - 1].type !== ts.type && (
                    <div
                      style={{
                        height: '60px',
                        borderLeft: '1px solid #BABFD4',
                        margin: '0 5px',
                      }}
                    />
                  )}
                  <div
                    className="face_assessment_preview-treatment-selector-item"
                    onClick={() => {
                      setSelectCategory(ts);
                      setRatio(ts.ratio);
                    }}
                    style={{
                      marginTop: ts.value === selectCategory.value && '8px',
                    }}
                  >
                    <div
                      className={classnames({
                        'face_assessment_preview-treatment-selector-item-img': true,
                        'face_assessment_preview-treatment-selector-item-img-selected':
                          ts.value === selectCategory.value,
                      })}
                      style={{
                        border:
                          ts.value === selectCategory.value &&
                          `2px solid ${primaryColor}`,
                      }}
                    >
                      <img src={ts.icon} alt="" />
                    </div>
                    <div
                      className="face_assessment_preview-treatment-selector-desc"
                      style={{
                        color:
                          ts.value === selectCategory.value && primaryColor,
                      }}
                    >
                      {ts.label}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
